import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Network, Check, Crown, Clock, Share2, Flame, Trophy, Linkedin,
} from 'lucide-react';
import { authAPI, subscriptionAPI } from '../api';
import { useGrowthFreeMode } from '../lib/growthMode';
import { consumePostSignupRedirect } from '../lib/postSignupRedirect';
import { canonical } from '../lib/seo';

const fmtMoney = (plan, currency) => {
  if (!plan) return null;
  return currency === 'USD'
    ? (plan.price_usd != null ? `$${plan.price_usd}` : null)
    : `₹${(plan.price || 0).toLocaleString('en-IN')}`;
};

// Real, verified user quotes only. Leave empty rather than fabricating social proof.
// Shape: { name, role, quote }
const TESTIMONIALS = [];

const Tag = ({ children }) => (
  <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold px-4 py-2 bg-zinc-900/50 border border-white/5 rounded-sm">
    {children}
  </span>
);

const Login = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState('INR');
  const freeMode = useGrowthFreeMode(); // growth free mode → no pricing section

  useEffect(() => {
    subscriptionAPI.getPlans()
      .then((res) => {
        const paid = (res.data?.plans || []).filter((p) => (p.price || 0) > 0).sort((a, b) => (a.price || 0) - (b.price || 0));
        setPlans(paid);
        if (res.data?.currency) setCurrency(res.data.currency);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) return;
        const response = await authAPI.getMe();
        if (response.data) navigate(consumePostSignupRedirect() || '/dashboard');
      } catch {
        localStorage.removeItem('session_token');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <Helmet>
        <title>Mock System Design Interview Practice | System Design Lab</title>
        <meta name="description" content="Do the system design interview 50 times before the real one. An AI interviewer grades your design like a FAANG panel — diagram, scalability, trade-offs — and shows you exactly what to fix. Start free, no credit card." />
        <link rel="canonical" href={canonical('/')} />
        <meta property="og:title" content="Mock System Design Interview Practice | System Design Lab" />
        <meta property="og:description" content="Do the system design interview 50 times before the real one. An AI interviewer grades your design like a FAANG panel — diagram, scalability, trade-offs. Start free, no credit card." />
        <meta property="og:url" content={canonical('/')} />
        <meta property="twitter:title" content="Mock System Design Interview Practice | System Design Lab" />
        <meta property="twitter:description" content="Do the system design interview 50 times before the real one. An AI interviewer grades your design like a FAANG panel. Start free, no credit card." />
        <meta property="twitter:url" content={canonical('/')} />
      </Helmet>

      {/* Glow accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">

        {/* ── Nav ─────────────────────────────────────────────────────────── */}
        <nav className="flex items-center justify-between py-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
              <Network className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-heading font-bold tracking-tighter">System Design Lab</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-400">
            <button onClick={() => navigate('/resources')} className="hover:text-white transition hidden md:block">Resources</button>
            <button onClick={() => navigate('/learn')} className="hover:text-white transition hidden md:block">Learn</button>
            {!freeMode && <a href="#pricing" className="hover:text-white transition hidden md:block">Pricing</a>}
            <button onClick={handleLogin} className="text-zinc-300 hover:text-white transition" data-testid="nav-login">Sign in</button>
            <Button onClick={handleLogin} className="bg-primary hover:bg-primary/90 text-white text-sm font-medium px-4 py-2 rounded-sm h-auto">
              Start free <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </div>
        </nav>

        {/* ── Hero: outcome + product shown ───────────────────────────────── */}
        <section className="grid md:grid-cols-2 gap-10 items-center py-12">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold text-zinc-400 bg-zinc-900/50 border border-white/5 rounded-full px-3 py-1.5 mb-5">
              <span className="text-primary">●</span> Prep for interviews at Google · Meta · Amazon · Stripe
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold tracking-tighter leading-[1.05] mb-5">
              Do the interview<br /><span className="text-primary">50 times</span> before<br />the real one.
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed mb-7 max-w-md">
              An AI interviewer that grades your system design like a real FAANG panel — diagram, scalability,
              and trade-offs. Walk in already knowing your weak spots.
            </p>
            <Button
              onClick={handleLogin}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white font-medium uppercase tracking-wide px-8 py-6 text-base rounded-sm transition-all active:scale-95"
              data-testid="login-button"
            >
              Start your first interview free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Link
              to="/try?from=landing"
              className="inline-flex items-center gap-2 align-middle mt-3 sm:mt-0 sm:ml-3 border border-white/10 hover:border-primary/40 text-zinc-300 hover:text-white text-sm font-medium px-6 py-4 rounded-sm transition"
              data-testid="hero-try-drill"
            >
              <Clock className="w-4 h-4 text-primary" /> Try a 5-minute drill — no signup
            </Link>
            <p className="text-xs text-zinc-600 mt-3">No credit card · graded in seconds</p>

            {/* Honest platform-fact strip (no fabricated engagement metrics) */}
            <div className="flex items-center gap-5 mt-8">
              <div><div className="text-2xl font-heading font-bold">15+</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Interview problems</div></div>
              <div className="w-px h-9 bg-white/10" />
              <div><div className="text-2xl font-heading font-bold">40+</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Learn modules</div></div>
              <div className="w-px h-9 bg-white/10" />
              <div><div className="text-2xl font-heading font-bold">5-dim</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">AI scorecard</div></div>
            </div>
          </div>

          {/* Product visual: diagram + live AI scorecard */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-cyan-500/10 blur-2xl opacity-40" />
            <div className="relative bg-zinc-900 border border-white/10 rounded-lg overflow-hidden shadow-2xl">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-black/40">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                <span className="ml-3 text-xs text-zinc-500 font-mono">Design Twitter&apos;s timeline · 38:12 left</span>
              </div>
              <div className="p-5 bg-[#0d0d0f]">
                <div className="grid grid-cols-3 gap-2 text-[10px] text-zinc-400 font-mono mb-3">
                  {[['Client', 'cyan'], ['API Gateway', 'orange'], ['Load Balancer', 'cyan'], ['Fan-out svc', 'zinc'], ['Redis cache', 'zinc'], ['Cassandra', 'zinc']].map(([label, c]) => (
                    <div key={label} className={`border rounded p-2 text-center ${
                      c === 'cyan' ? 'border-cyan-500/30 bg-cyan-500/5' : c === 'orange' ? 'border-primary/30 bg-primary/5' : 'border-zinc-700 bg-zinc-800/40'
                    }`}>{label}</div>
                  ))}
                </div>
                <div className="bg-black border border-primary/30 rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">AI Scorecard</span>
                    <span className="text-2xl font-heading font-bold text-primary">8.2<span className="text-sm text-zinc-600">/10</span></span>
                  </div>
                  <div className="space-y-1.5 text-[11px]">
                    {[['Architecture', 85], ['Scalability', 80], ['Trade-offs', 70]].map(([dim, w]) => (
                      <div key={dim}>
                        <div className="flex justify-between text-zinc-300"><span>{dim}</span><span className="font-mono text-primary">{(w / 10).toFixed(1)}</span></div>
                        <div className="h-1 bg-zinc-800 rounded"><div className="h-full bg-primary rounded" style={{ width: `${w}%` }} /></div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2 italic">&ldquo;Strong fan-out-on-write choice. You missed the celebrity hot-key problem — address it.&rdquo;</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── The loop ────────────────────────────────────────────────────── */}
        <section className="py-16">
          <p className="text-center text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">The loop that gets you ready</p>
          <h2 className="text-center text-3xl md:text-4xl font-heading font-bold tracking-tighter mb-12">
            Practice → Get scored → Share → Repeat
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { n: '01', t: 'Pick a problem', b: 'Real interview questions — Twitter, YouTube, URL shortener and more.' },
              { n: '02', t: 'Design under the clock', b: 'Drag-and-drop diagram + an AI interviewer asking follow-ups.' },
              { n: '03', t: 'Get a real scorecard', b: '5 weighted dimensions, with the reasoning. Know exactly what to fix.' },
              { n: '04', t: 'Share & climb', b: 'Post your score, challenge a friend, rise up the leaderboard.', hot: true },
            ].map((s) => (
              <div key={s.n} className={`bg-zinc-900/50 border rounded-md p-6 ${s.hot ? 'border-primary/40' : 'border-white/5'}`}>
                <div className="text-primary font-heading font-bold text-3xl mb-3">{s.n}</div>
                <h3 className="font-heading font-semibold mb-1.5">{s.t} {s.hot && <Flame className="inline w-4 h-4 text-primary" />}</h3>
                <p className="text-sm text-zinc-400">{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Shareable scorecard ─────────────────────────────────────────── */}
        <section className="py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-widest text-primary font-bold">Every score is a flex</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tighter mt-3 mb-4">Turn a good score into a share.</h2>
            <p className="text-zinc-400 leading-relaxed mb-5">
              Beat a hard problem? Get a clean, branded scorecard you can drop on LinkedIn or send to a friend in one tap.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 bg-[#0a66c2] text-white text-sm font-medium px-4 py-2.5 rounded-sm"><Linkedin className="w-4 h-4" /> Share on LinkedIn</span>
              <Link to="/try?from=landing_challenge" className="inline-flex items-center gap-2 border border-white/10 text-zinc-300 text-sm font-medium px-4 py-2.5 rounded-sm hover:border-primary/40 hover:text-white transition" data-testid="landing-challenge-friend"><Share2 className="w-4 h-4" /> Challenge a friend</Link>
            </div>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-lg p-7 max-w-sm mx-auto w-full">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-primary rounded-sm flex items-center justify-center"><Network className="w-4 h-4 text-white" /></div>
                <span className="font-heading font-bold tracking-tighter text-sm">System Design Lab</span>
              </div>
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-1">Design Twitter&apos;s timeline</p>
            <div className="text-6xl font-heading font-bold text-primary mb-1">8.2<span className="text-2xl text-zinc-600">/10</span></div>
            <p className="text-sm text-zinc-400 mb-5">A scorecard worth sharing.</p>
            <div className="flex gap-2 text-[10px] font-mono text-zinc-400">
              <span className="bg-zinc-800 rounded px-2 py-1">⚡ Architecture 8.5</span>
              <span className="bg-zinc-800 rounded px-2 py-1">📈 Scale 8.0</span>
            </div>
            <p className="text-[10px] text-zinc-600 mt-5">systemdesignlab.in · Can you beat it?</p>
          </div>
        </section>

        {/* ── Leaderboard + streaks ───────────────────────────────────────── */}
        <section className="py-16 grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1 bg-zinc-900/50 border border-white/5 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" /> This week&apos;s leaderboard</span>
              <span className="text-xs text-zinc-500">resets Sun</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded p-2.5"><span className="font-mono text-primary w-5">1</span><span className="flex-1">Climb to the top</span><span className="font-mono text-primary">9.1</span><span>🔥</span></div>
              <div className="flex items-center gap-3 bg-zinc-800/40 rounded p-2.5"><span className="font-mono text-zinc-500 w-5">2</span><span className="flex-1 text-zinc-400">Build a streak</span><span className="font-mono text-zinc-300">8.8</span><span>🔥</span></div>
              <div className="flex items-center gap-3 bg-zinc-800/40 rounded p-2.5"><span className="font-mono text-zinc-500 w-5">3</span><span className="flex-1 text-zinc-400">Your spot?</span><span className="font-mono text-zinc-600">—</span><span className="text-zinc-600">🔥</span></div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <span className="text-xs uppercase tracking-widest text-primary font-bold">Make prep a habit</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tighter mt-3 mb-4">Streaks, leaderboards, and healthy rivalry.</h2>
            <p className="text-zinc-400 leading-relaxed">
              Daily streaks keep you coming back. The weekly leaderboard makes it competitive. Public profiles let you
              show off a portfolio of designs — a link worth putting on your résumé.
            </p>
          </div>
        </section>

        {/* ── Free resources funnel ───────────────────────────────────────── */}
        <section className="py-14 bg-zinc-900/50 border border-white/5 rounded-lg p-8 text-center mb-4">
          <span className="text-xs uppercase tracking-widest text-cyan-400 font-bold">Free &amp; public</span>
          <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tighter mt-3 mb-3">Not ready to practice? Start by reading.</h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6">
            In-depth system design guides and case-study walkthroughs — free, no signup.
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-left max-w-3xl mx-auto">
            {[['Case study', 'text-rose-400', 'Design a URL shortener'], ['Fundamentals', 'text-primary', 'Caching strategies explained'], ['Scalability', 'text-purple-400', 'Kafka vs RabbitMQ vs SQS']].map(([cat, color, title]) => (
              <button key={title} onClick={() => navigate('/resources')} className="border border-white/5 rounded p-4 text-left hover:border-primary/30 transition">
                <span className={`text-[10px] uppercase font-bold ${color}`}>{cat}</span>
                <p className="font-heading font-semibold text-sm mt-1">{title}</p>
              </button>
            ))}
          </div>
          <button onClick={() => navigate('/resources')} className="mt-6 text-sm text-cyan-400 hover:underline">Browse all resources →</button>
        </section>

        {/* ── Testimonials (only render with real, verified quotes) ────────── */}
        {TESTIMONIALS.length > 0 && (
          <section className="py-16">
            <h2 className="text-center text-3xl font-heading font-bold tracking-tighter mb-10">Engineers who practiced here</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {TESTIMONIALS.map((t) => (
                <div key={t.name} className="bg-zinc-900/50 border border-white/5 rounded-md p-6">
                  <p className="text-sm text-zinc-300 leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-zinc-500">{t.role}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Pricing (hidden in growth free mode) ─────────────────────────── */}
        {!freeMode && (
        <section className="py-16 max-w-3xl mx-auto" id="pricing">
          <div className="text-center mb-10">
            <Tag>Simple Pricing</Tag>
            <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tighter mt-6 mb-4">Start Free, Upgrade When Ready</h2>
            <p className="text-lg text-zinc-400 max-w-xl mx-auto">
              Free forever — all learning content plus a practice interview every week. Upgrade for unlimited access.
            </p>
            {plans.some((p) => p.price_usd != null) && (
              <div className="flex items-center justify-center gap-1 mt-5">
                {['INR', 'USD'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    className={`text-xs px-3 py-1 rounded-sm border transition-colors ${
                      currency === c ? 'bg-primary text-white border-primary' : 'border-zinc-700 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {c === 'INR' ? '₹ INR' : '$ USD'}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Free */}
            <div className="bg-zinc-900/50 border border-white/5 rounded-md p-8">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-cyan-400" />
                <h4 className="text-xl font-heading font-medium">Free</h4>
              </div>
              <div className="text-4xl font-heading font-bold mb-2">{currency === 'USD' ? '$0' : '₹0'}</div>
              <p className="text-sm text-zinc-500 mb-6">Free forever · no card required</p>
              <ul className="space-y-3 mb-8">
                {['All learning modules — always free', 'All quiz questions', 'Browse & post community solutions', '7-day trial: 3 AI-graded interviews', 'Then 1 free interview every week'].map((text) => (
                  <li key={text} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" /><span>{text}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={handleLogin} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 rounded-sm uppercase tracking-wide font-medium text-sm" data-testid="start-free-trial">
                Start Free Trial
              </Button>
            </div>

            {/* Premium */}
            <div className="relative bg-zinc-900/50 border-2 border-orange-500/60 rounded-md p-8">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white text-[11px] font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">Most Popular</span>
              </div>
              <div className="flex items-center gap-3 mb-4 mt-2">
                <Crown className="w-6 h-6 text-primary" />
                <h4 className="text-xl font-heading font-medium">Premium</h4>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                {plans[1] && fmtMoney(plans[1], currency) && (
                  <span className="text-xl text-zinc-600 line-through">{fmtMoney(plans[1], currency)}</span>
                )}
                <span className="text-4xl font-heading font-bold text-primary">
                  {fmtMoney(plans[0], currency) || (currency === 'USD' ? '$19' : '₹999')}
                </span>
              </div>
              <p className="text-sm text-zinc-500 mb-6">{(plans[0]?.duration_days || 90)} days · Everything unlimited</p>
              <ul className="space-y-3 mb-8">
                {['Unlimited AI-graded interviews', 'AI interviewer chat + voice mode', 'Unlimited quizzes + weak-area analytics', 'Post & share diagrams', 'Performance analytics dashboard'].map((text) => (
                  <li key={text} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" /><span>{text}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={handleLogin} className="w-full bg-primary hover:bg-primary/90 text-white font-medium rounded-sm uppercase tracking-wide text-sm active:scale-95 transition-all" data-testid="get-premium">
                Get Premium
              </Button>
            </div>
          </div>
        </section>
        )}

        {/* ── Final CTA ───────────────────────────────────────────────────── */}
        <section className="py-20 text-center border-t border-white/5">
          <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tighter mb-4">Your interview won&apos;t wait. Neither should you.</h2>
          <p className="text-zinc-400 mb-7 max-w-lg mx-auto">Start your first AI-graded mock interview free. See your score in seconds.</p>
          <Button
            onClick={handleLogin}
            className="bg-primary hover:bg-primary/90 text-white font-medium uppercase tracking-wide px-10 py-6 text-base rounded-sm transition-all active:scale-95"
            data-testid="footer-cta"
          >
            Start practicing free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-xs text-zinc-600 mt-3">{freeMode ? 'No credit card · free during launch · unlimited 5-minute drills' : 'No credit card · 7-day trial · 3 graded interviews'}</p>
        </section>

        <footer className="border-t border-white/5 py-8 text-center text-xs text-zinc-600">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button onClick={() => navigate('/resources')} className="hover:text-zinc-300">Resources</button>
            <button onClick={() => navigate('/learn')} className="hover:text-zinc-300">Learn</button>
            {!freeMode && <a href="#pricing" className="hover:text-zinc-300">Pricing</a>}
            <span>· System Design Lab</span>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Login;
